import { Suspense, useRef, useState } from "react";
import styled from "styled-components";
import {
  ChartCandlestick,
  Grid3x3,
  Layers,
  LucideIcon,
  Radio,
} from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { hexToRGB } from "@shared/utils/misc";
import ArtStage from "features/onboarding/art/ArtStage";
import { ArtPlayback } from "features/onboarding/art/live";
import MethodArt from "features/onboarding/art/MethodArt";
import { motionAllowed } from "features/onboarding/art/kit";
import { TradingIntent } from "features/onboarding/guided/intent";
import { reveal } from "features/onboarding/common/onboardingUi";

// The cornix.io home hero, adapted for the sign-in pages: a short pitch and a
// live illustration of one trading method at a time (the same animated art
// the onboarding uses), advancing on its own like the site's workflow tabs.

const METHODS = [
  { key: "signals", icon: Radio, name: "intent.signals.name" },
  { key: "grid", icon: Grid3x3, name: "intent.grid.name" },
  { key: "dca", icon: Layers, name: "intent.dca.name" },
  {
    key: "tradingview",
    icon: ChartCandlestick,
    name: "intent.tradingview.name",
  },
] as const satisfies readonly {
  key: TradingIntent;
  icon: LucideIcon;
  name: string;
}[];
const SENTENCE = /[^.!?]+[.!?]*/g;

interface AuthHeroProps {
  logo?: React.ReactNode;
  footer?: React.ReactNode;
}

const AuthHero = ({ logo, footer }: AuthHeroProps) => (
  <Panel>
    <Glow aria-hidden />
    <Top>{logo}</Top>
    <Suspense fallback={<Body />}>
      <HeroBody />
    </Suspense>
    <Bottom>{footer}</Bottom>
  </Panel>
);

export default AuthHero;

const HeroBody = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const [active, setActive] = useState(0);
  const progress = useRef<HTMLSpanElement>(null);
  const autoAdvance = motionAllowed();

  // One line per sentence ("Your first bot." / "Without the guesswork."),
  // with the last word in the brand blue as on cornix.io.
  const title = t("guided.welcome_title");
  // No lookbehind: older Safari versions can't parse it.
  const sentences = title.match(SENTENCE)?.map((part) => part.trim()) ?? [
    title,
  ];
  const lastSentence = sentences.pop() ?? "";
  const lastSpace = lastSentence.lastIndexOf(" ");

  return (
    <Body>
      <Badge>
        <Dot />
        {t("guided.welcome_badge")}
      </Badge>
      <Headline>
        {sentences.map((sentence) => (
          <span key={sentence}>{sentence} </span>
        ))}
        <span>
          {lastSentence.slice(0, lastSpace + 1)}
          <Accent>{lastSentence.slice(lastSpace + 1)}</Accent>
        </span>
      </Headline>
      <Lead>{t("guided.welcome_description")}</Lead>
      <Showcase>
        <Stage height={360} plain>
          {METHODS.map(({ key, name }, index) => (
            <Scene
              role="tabpanel"
              aria-label={t(name)}
              key={key}
              $active={index === active}
              aria-hidden={index !== active}
            >
              <ArtPlayback.Provider
                value={
                  autoAdvance
                    ? {
                        onComplete: () =>
                          setActive((current) =>
                            current === index
                              ? (current + 1) % METHODS.length
                              : current
                          ),
                        onProgress: (value) => {
                          if (index === active && progress.current)
                            progress.current.style.transform = `scaleX(${value})`;
                        },
                      }
                    : null
                }
              >
                <MethodArt method={key} play={index === active} />
              </ArtPlayback.Provider>
            </Scene>
          ))}
        </Stage>
        <Tabs role="tablist" aria-label={t("intent.selected")}>
          {METHODS.map(({ key, icon: Icon, name }, index) => (
            <Tab
              key={key}
              type="button"
              role="tab"
              aria-selected={index === active}
              $active={index === active}
              onClick={() => setActive(index)}
            >
              <Icon size={15} strokeWidth={1.75} aria-hidden />
              {t(name)}
              {index === active && autoAdvance && (
                <Progress key={active} ref={progress} aria-hidden />
              )}
            </Tab>
          ))}
        </Tabs>
      </Showcase>
    </Body>
  );
};

const Panel = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 32px clamp(32px, 4vw, 56px) 28px;
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  background: ${(p) =>
    p.theme.isDarkMode
      ? p.theme.colors.cardBgColor
      : p.theme.colors.cardLightBgColor};
`;
// The site's hero light: a blue pool under the headline with mint and violet
// highlights in the corners.
const Glow = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: ${({ theme }) =>
    [
      `radial-gradient(55% 45% at 50% 72%, ${hexToRGB(
        theme.colors.primaryBlue,
        0.14
      )}, transparent 70%)`,
      `radial-gradient(34% 30% at 88% 12%, ${hexToRGB(
        theme.colors.lightGreen,
        theme.isDarkMode ? 0.08 : 0.16
      )}, transparent 70%)`,
      `radial-gradient(36% 34% at 8% 30%, ${hexToRGB(
        theme.colors.purple,
        theme.isDarkMode ? 0.1 : 0.08
      )}, transparent 70%)`,
    ].join(", ")};
`;
const Top = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  min-height: 32px;
`;
const Bottom = styled.div`
  flex: none;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
`;
const Body = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 600px;
  margin-inline: auto;
  padding: 28px 0;
`;
const Badge = styled.span`
  ${reveal(0)};
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px;
  border-radius: 6px;
  border: 1px solid ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.16)};
  background: ${(p) => p.theme.colors.cardBgColor};
  color: ${(p) => p.theme.colors.mediumText};
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.yellow};
`;
const Headline = styled.h1`
  ${reveal(1)};
  margin: 20px 0 0;
  max-width: 22ch;
  > span {
    display: block;
  }
  color: ${(p) => p.theme.colors.titleColor};
  font-size: clamp(32px, 2.9vw, 44px);
  line-height: 1.12;
  font-weight: ${(p) => p.theme.typography.fontWeights.EXTRA_BOLD};
  letter-spacing: -0.025em;
  text-wrap: balance;
`;
const Accent = styled.span`
  color: ${(p) => p.theme.colors.primaryBlue};
`;
const Lead = styled.p`
  ${reveal(2)};
  margin: 16px 0 0;
  max-width: 36rem;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 16px;
  line-height: 1.6;
`;
const Showcase = styled.div`
  ${reveal(3)};
  margin-top: 28px;
`;
const Stage = styled(ArtStage)`
  && {
    height: clamp(220px, 40vh, 380px);
  }
`;
const Scene = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: ${(p) => (p.$active ? 1 : 0)};
  pointer-events: none;
  transition: opacity 500ms ease-in-out;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
`;
const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid
    ${(p) =>
      p.$active
        ? hexToRGB(p.theme.colors.primaryBlue, 0.35)
        : p.theme.colors.cardBorderColor};
  background: ${(p) =>
    p.$active ? p.theme.colors.cardBgColor : "transparent"};
  color: ${(p) =>
    p.$active ? p.theme.colors.titleColor : p.theme.colors.secondaryText};
  font: inherit;
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease,
    color 150ms ease;
  svg {
    color: ${(p) => (p.$active ? p.theme.colors.primaryBlue : "currentColor")};
  }
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
    border-color: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.35)};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 2px;
  }
`;
const Progress = styled.span`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 0;
  height: 2px;
  border-radius: 2px;
  background: ${(p) => p.theme.colors.primaryBlue};
  transform-origin: left center;
  transform: scaleX(0);
`;
